import React from 'react'
import styled from 'styled-components/native'

import { Collapsible } from 'APP/Converse/Collapsible'
import IconCard from 'APP/Converse/IconCard'
import type { Level } from 'APP/Converse/Scale'
import ScaleCard from 'APP/Converse/ScaleCard'
import {
  OutlinedTypeEnum,
  OutlinedTypeEnumValues,
  RichContentTypeEnum,
  RichContentUnion,
  ScaleLevel,
} from 'APP/Services/Mattermost/types'
import { Space, SpaceSize } from 'APP/Converse/Space'
import Card from 'APP/Converse/Card'
import type { CardVariant } from 'APP/Converse/Card/style'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import { useMarkdownStyles } from 'APP/Hooks/useMarkdownStyles'
import IntakeWaitingSteps from '../IntakeWaitingSteps'
import AppointmentCardStatic from 'APP/Components/AppointmentCard/AppointmentCardStatic'

const outlinedVariantMap: Record<OutlinedTypeEnumValues, CardVariant> = {
  [OutlinedTypeEnum.Default]: 'success',
  [OutlinedTypeEnum.Info]: 'info',
  [OutlinedTypeEnum.Action]: 'accent',
  [OutlinedTypeEnum.Negative]: 'neutral',
  [OutlinedTypeEnum.Highlight]: 'highlight',
}

const RichContentItemContainer = styled.View`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin};
`

const RichContentGroupedSectionContainer = styled.View`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin};
`

const StyledAppointmentCardStatic = styled(AppointmentCardStatic)`
  margin: 0;
`

export type StructuredDescriptionProps = {
  postId?: string
  structuredDescription: RichContentUnion[]
  currentStepIndex?: number
  imageHeight?: number
}

// Maps dialogue.messages ScaleCard levels to the format expected by the Scale component
// It also limits the number of levels to 4, as that's the maximum required by dialogue.messages
const toScaleLevels = (levels: ScaleLevel[]): Level[] =>
  Array.isArray(levels)
    ? levels.slice(0, 4).map(({ label, value }) => ({ value, label: label ?? undefined }))
    : []

type RichContentItemProps = {
  richContent: RichContentUnion
  markdownStyles: ReturnType<typeof useMarkdownStyles>
  currentStepIndex?: number
  imageHeight?: number
}

const RichContentItem = ({
  richContent,
  markdownStyles,
  currentStepIndex,
  imageHeight,
}: RichContentItemProps) => {
  switch (richContent.type) {
    case RichContentTypeEnum.IconCard:
      return (
        <IconCard
          title={richContent.title}
          leftIcon={{
            src: richContent.icon_type ?? 'checklist',
            background: 'serviceDetailCardBg',
          }}
          description={richContent.description}
          testID="INTAKE_QUESTION_ICON_CARD"
        />
      )

    case RichContentTypeEnum.Collapsible:
      return (
        <Collapsible
          title={richContent.title}
          children={richContent.description}
          analyticsName={richContent.analytics_name}
        />
      )

    case RichContentTypeEnum.Outlined:
      return (
        <Card
          variant={outlinedVariantMap[richContent.variant ?? OutlinedTypeEnum.Default]}
          testID="INTAKE_QUESTION_OUTLINED_CARD"
        >
          {richContent.title && (
            <DialogueMarkdown markdown={richContent.title} styles={{}} containerStyles={{}} />
          )}
          {richContent.description && (
            <DialogueMarkdown markdown={richContent.description} styles={{}} containerStyles={{}} />
          )}
        </Card>
      )

    case RichContentTypeEnum.Scale:
      return (
        <ScaleCard
          subtitle={richContent.subtitle}
          title={richContent.title}
          scale={{
            levels: toScaleLevels(richContent.levels),
            value: richContent.selected,
          }}
          children={richContent.description}
        />
      )

    case RichContentTypeEnum.Text:
      return (
        <DialogueMarkdown
          markdown={richContent.description}
          styles={
            imageHeight
              ? {
                  ...markdownStyles,
                  imageWrapper: {
                    height: imageHeight,
                  },
                }
              : markdownStyles
          }
          containerStyles={{}}
        />
      )

    case RichContentTypeEnum.Steps:
      return (
        <IntakeWaitingSteps steps={richContent.steps} currentStepIndex={currentStepIndex ?? 0} />
      )

    case RichContentTypeEnum.Appointment:
      return (
        <StyledAppointmentCardStatic
          date={richContent.aside}
          timeslot={richContent.title}
          providerName={richContent.heading}
          providerRole={richContent.subheading}
          issueTypeId={richContent.footer}
          reasonForConsult={richContent.content}
          testID="APPT_CARD_STATIC"
        />
      )

    case RichContentTypeEnum.GroupedSection:
      return (
        <RichContentGroupedSectionContainer>
          {richContent.items?.map((item, index) => {
            const key = `grouped-${index}`
            return (
              <React.Fragment key={key}>
                {index > 0 && <Space size={SpaceSize.Small} />}
                <RichContentItem
                  richContent={item}
                  markdownStyles={markdownStyles}
                  currentStepIndex={currentStepIndex}
                  imageHeight={imageHeight}
                />
              </React.Fragment>
            )
          })}
        </RichContentGroupedSectionContainer>
      )

    default:
      return null
  }
}

export const StructuredDescription = ({
  postId,
  structuredDescription,
  currentStepIndex,
  imageHeight,
}: StructuredDescriptionProps) => {
  const markdownStyles = useMarkdownStyles({ variant: 'bodyNormal' })

  return (
    <RichContentItemContainer>
      {structuredDescription.map((richContent, index) => {
        const key = `${postId}-${index}`
        const leadingSpaceSize =
          richContent.type === RichContentTypeEnum.GroupedSection
            ? SpaceSize.Small
            : SpaceSize.Large

        return (
          <React.Fragment key={key}>
            {index > 0 && <Space size={leadingSpaceSize} />}
            <RichContentItem
              richContent={richContent}
              markdownStyles={markdownStyles}
              currentStepIndex={currentStepIndex}
              imageHeight={imageHeight}
            />
          </React.Fragment>
        )
      })}
    </RichContentItemContainer>
  )
}
